/**
 * Theme context. Single owner of the `data-theme` attribute and the `cortex_theme` localStorage
 * key. Three choices, as in the redesign's user menu: "system" (no attribute, the OS decides via
 * prefers-color-scheme), "light" and "dark" (explicit attribute, wins over the OS). The older
 * `dark` / `toggle` pair is kept for any caller that only knows two states.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type ThemeChoice = "system" | "light" | "dark";

type ThemeState = { choice: ThemeChoice; setChoice: (c: ThemeChoice) => void; dark: boolean; toggle: () => void };

const ThemeContext = createContext<ThemeState | null>(null);

function systemPrefersDark(): boolean {
  try { return window.matchMedia("(prefers-color-scheme: dark)").matches; } catch { return true; }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => {
    try {
      const s = localStorage.getItem("cortex_theme");
      return s === "light" || s === "dark" || s === "system" ? s : "dark"; // default dark
    } catch {
      return "dark";
    }
  });
  const [osDark, setOsDark] = useState(systemPrefersDark);

  useEffect(() => {
    try { localStorage.setItem("cortex_theme", choice); } catch { /* ignore */ }
    if (choice === "system") document.documentElement.removeAttribute("data-theme");
    else document.documentElement.setAttribute("data-theme", choice);
  }, [choice]);

  useEffect(() => {
    let mq: MediaQueryList | null = null;
    try { mq = window.matchMedia("(prefers-color-scheme: dark)"); } catch { return; }
    const fn = (e: MediaQueryListEvent) => setOsDark(e.matches);
    mq.addEventListener("change", fn);
    return () => mq?.removeEventListener("change", fn);
  }, []);

  const dark = choice === "system" ? osDark : choice === "dark";
  return (
    <ThemeContext.Provider value={{
      choice,
      setChoice: setChoiceState,
      dark,
      toggle: () => setChoiceState(dark ? "light" : "dark"),
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}
